import { atom } from 'jotai';

export type AddUsersDialogStep = 'users' | 'facilities';

export interface AddUsersFormData {
  roleId: string;
  roleName: string;
}

const getInitialFormData = (): AddUsersFormData => ({
  roleId: '',
  roleName: '',
});

export const $addUsersDialogOpen = atom(false);
export const $addUsersDialogStep = atom<AddUsersDialogStep>('users');
export const $addUsersFormData = atom<AddUsersFormData>(getInitialFormData());
export const $selectedUserIds = atom<Set<string>>(new Set<string>());
export const $selectedFacilityIds = atom<Set<string>>(new Set<string>());
export const $isAddingUsers = atom(false);
export const $defaultFacilityId = atom<string | null>(null);
export const $existingUserIds = atom<Set<string>>(new Set<string>());

export const $isSingleFacilityMode = atom(
  (get) => get($defaultFacilityId) !== null,
);

export const $openAddUsersDialog = atom(
  null,
  (
    _get,
    set,
    params: {
      roleId: string;
      roleName: string;
      defaultFacilityId?: string;
      existingUserIds?: Set<string>;
    },
  ) => {
    set($addUsersFormData, {
      roleId: params.roleId,
      roleName: params.roleName,
    });
    set($addUsersDialogStep, 'users');
    set($selectedUserIds, new Set());
    set($existingUserIds, params.existingUserIds ?? new Set());

    if (params.defaultFacilityId) {
      set($defaultFacilityId, params.defaultFacilityId);
      set($selectedFacilityIds, new Set([params.defaultFacilityId]));
    } else {
      set($defaultFacilityId, null);
      set($selectedFacilityIds, new Set());
    }

    set($addUsersDialogOpen, true);
  },
);

export const $closeAddUsersDialog = atom(null, (_get, set) => {
  set($addUsersDialogOpen, false);
  set($addUsersFormData, getInitialFormData());
  set($addUsersDialogStep, 'users');
  set($selectedUserIds, new Set());
  set($selectedFacilityIds, new Set());
  set($defaultFacilityId, null);
  set($existingUserIds, new Set());
});

export const $goToFacilitiesStep = atom(null, (_get, set) => {
  set($addUsersDialogStep, 'facilities');
});

export const $goToUsersStep = atom(null, (_get, set) => {
  set($addUsersDialogStep, 'users');
});

export const $toggleUserSelection = atom(null, (get, set, userId: string) => {
  const current = get($selectedUserIds);
  const newSet = new Set(current);
  if (newSet.has(userId)) {
    newSet.delete(userId);
  } else {
    newSet.add(userId);
  }
  set($selectedUserIds, newSet);
});

export const $toggleFacilitySelection = atom(
  null,
  (get, set, facilityId: string) => {
    const current = get($selectedFacilityIds);
    const newSet = new Set(current);
    if (newSet.has(facilityId)) {
      newSet.delete(facilityId);
    } else {
      newSet.add(facilityId);
    }
    set($selectedFacilityIds, newSet);
  },
);

export const $clearUserSelections = atom(null, (_get, set) => {
  set($selectedUserIds, new Set());
});

export const $clearFacilitySelections = atom(null, (_get, set) => {
  set($selectedFacilityIds, new Set());
});

export const $selectedUserCount = atom((get) => get($selectedUserIds).size);
export const $selectedFacilityCount = atom(
  (get) => get($selectedFacilityIds).size,
);

export const $canProceedToFacilities = atom((get) => {
  const selectedUsers = get($selectedUserIds);
  return selectedUsers.size > 0;
});

export const $canSave = atom((get) => {
  const selectedUsers = get($selectedUserIds);
  const isSingleFacility = get($isSingleFacilityMode);
  if (isSingleFacility) {
    return selectedUsers.size > 0;
  }
  const selectedFacilities = get($selectedFacilityIds);
  return selectedUsers.size > 0 && selectedFacilities.size > 0;
});
